import type { ElementType, ReactNode } from "react";
import { hasInlineRich, renderInline } from "@/lib/inlineRich";

// Per-element CMS overlay for designed sections (Option A): renders the DB
// override when present, else the hardcoded copy, and stamps data-cms-key so
// the admin preview's edit mode makes the element editable in place. Works
// for any tag via `as`.
export function CmsText({
  c,
  k,
  as,
  children,
  className,
}: {
  c: Record<string, string>;
  k: string;
  as?: ElementType;
  children: ReactNode;
  className?: string;
}) {
  const Tag: ElementType = as ?? "span";
  const v = c[k]?.trim();
  // The SOURCE this element was rendered from, for the in-place editor. A
  // value carrying "[label](/page)" or "**bold**" renders as the words alone,
  // so an editor reading the rendered text back would post the words and drop
  // the formatting. data-cms-raw gives it the real string to edit.
  //
  // Only when it differs from what is displayed: on ordinary copy the
  // attribute would just repeat the paragraph into the markup.
  const raw = v ?? (typeof children === "string" ? children : undefined);
  const needsRaw = raw !== undefined && hasInlineRich(raw);
  // An override goes through the inline renderer so an editor can bold a word
  // or link a phrase mid-sentence; plain copy comes back unchanged. The
  // hardcoded children are already JSX and are passed through as they are.
  return (
    <Tag data-cms-key={k} data-cms-raw={needsRaw ? raw : undefined} className={className}>
      {v ? renderInline(v) : children}
    </Tag>
  );
}

// Image counterpart — use for src, and stamp data-cms-img={k} on the <img>.
export const cmsImg = (c: Record<string, string>, k: string, fallback: string): string =>
  c[k]?.trim() || fallback;

// Alt-text counterpart — the admin ALT button saves alt under "<img key>.alt".
// Falls back to the code-provided alt when there's no override.
export const cmsAlt = (c: Record<string, string>, k: string, fallback: string): string =>
  c[`${k}.alt`]?.trim() || fallback;
